/* eslint-disable no-case-declarations */
import React, { lazy, Suspense } from 'react'
import { Provider } from 'react-redux'
import { Elements } from '@stripe/react-stripe-js'
import { stripePromise } from '@/public/helpers/stripe'
import { GoogleReCaptchaProvider, GoogleReCaptchaVerifier } from '@/public/recaptcha'
import Portal from '@/shared/Portal'
import store from '@/public/redux/store'
import CompanyNewsletterForm from './CompanyNewsletterForm'

// Declared once, here, never inside Loader. ReactRailsUJS re-renders Loader on
// every mountComponents() call, and a lazy() made during render is a new
// component type each time: React suspends on it, hiding the mounted tree and
// tearing down its layout effects -- which destroys and redraws checkout's
// Stripe fields under a member who is already using them.
const Assessment = lazy(() => import('@/public/assessment'))
const JoinButton = lazy(() => import('@/public/product/JoinButton'))
const CartButton = lazy(() => import('@/public/cart/CartButton'))
const CartDrawer = lazy(() => import('@/public/cart/CartDrawer'))
const CartMembershipToggle = lazy(() => import('@/public/product/CartMembershipToggle'))
const Checkout = lazy(() => import('@/public/checkout/Checkout'))
const CheckoutElements = lazy(() => import('@/public/checkout/CheckoutElements'))
const NewsletterForm = lazy(() => import('@/public/NewsletterForm'))
const PriceBlockWithQuantity = lazy(() => import('@/public/product/PriceBlockWithQuantity'))
const PriceBlockWithoutQuantity = lazy(() => import('@/public/product/PriceBlockWithoutQuantity'))
const ProductCardButtons = lazy(() => import('@/public/product/ProductCardButtons'))
const AddToCartButton = lazy(() => import('@/public/product/AddToCartButton'))
const ProductUtilityActions = lazy(() => import('@/public/product/ProductUtilityActions'))
const UpdatePaymentMethodForm = lazy(() => import('@/public/UpdatePaymentMethodForm'))
const Recommendations = lazy(() => import('@/public/product/Recommendations'))
const AutoAddToCart = lazy(() => import('@/public/product/AutoAddToCart'))
const PhoneInput = lazy(() => import('@/shared/PhoneInput'))
const StateLicenseMap = lazy(() => import('@/public/StateLicenseMap'))

export default function Loader({ component = null, data }) {
  if (component === null || 'name' in component === false) {
    if (console) {
      // eslint-disable-next-line no-console
      console.error('YOU MUST PASS IN COMPONENT INFO')
    }
    return ''
  }

  const renderComponent = () => {
    switch (true) {
      case component.name === 'Assessment':
        return (
          <Portal
            Component={Assessment}
            container={document.getElementById(component?.root || '')}
            data={data}
            debug={component.debug}
          />
        )

      case component.name === 'JoinButton':
        return (
          <Portal
            Component={JoinButton}
            container={document.getElementById(component?.root || '')}
            product={data.product}
            text={data.text}
            className={data.className}
            disabled={data.disabled}
            subscription={data.subscription}
            debug={component?.debug}
            hidden={data.hidden}
          />
        )

      case component.name === 'CartButton':
        return (
          <Portal
            Component={CartButton}
            container={document.getElementById(component?.root || '')}
            userCartId={data.cart_id}
            mobile={component?.mobile}
            debug={component?.debug}
          />
        )

      case component.name === 'CartDrawer':
        return (
          <CartDrawer
            checkoutUrl={data.checkout_url}
            storeDefaultUrl={data.store_default_url}
            customerIsMember={data.customer_is_member}
            monthlyMembership={data.monthly_membership}
            annualMembership={data.annual_membership}
            membershipPlansEnabled={data.membership_plans_enabled}
            userSponsoredAllowance={data.user_sponsored_allowance}
            virtualVisits={data.virtual_visits}
            debug={component?.debug}
          />
        )

      case component.name === 'Checkout':
        // The Elements provider, its options and the membership-boundary
        // rebuild all live in CheckoutElements (SS-2918). They were inline here
        // and could not stay: deciding the mode needs the LIVE cart, and this
        // switch only ever sees the render-time snapshot Rails passed in.
        return (
          <CheckoutElements stripePromise={stripePromise} renderTimeCart={data.cart}>
            <Portal
              Component={Checkout}
              container={document.getElementById(component?.root || '')}
              knownCustomer={data.customer}
              cart={data.cart}
              states={data.states}
              provinces={data.provinces}
              countries={data.countries}
              store_default_path={data.store_default_path}
              confirmation_path={data.confirmation_path}
              terms_policy_modal_id={data.terms_policy_modal_id}
              cc_logos_url={data.cc_logos_url}
              shipping_costs={data.shipping_costs}
              debug={component?.debug}
              activeDiscountCodePresent={data.active_discount_code_present}
              membershipAgreementUrl={data.membership_agreement_url}
              hipaaPrivacyTermsUrl={data.hipaa_privacy_terms_url}
              monthlyMembership={data.monthly_membership}
              annualMembership={data.annual_membership}
              membershipPlansEnabled={data.membership_plans_enabled}
              userSponsoredAllowance={data.user_sponsored_allowance}
              virtualVisits={data.virtual_visits}
            />
          </CheckoutElements>
        )

      case component.name === 'CartMembershipToggle':
        return (
          <Portal
            Component={CartMembershipToggle}
            container={document.getElementById(component?.root || '')}
            product={data.product}
            debug={component?.debug}
          />
        )

      case component.name === 'UpdatePaymentMethodForm':
        return (
          <Elements stripe={stripePromise}>
            <Portal
              Component={UpdatePaymentMethodForm}
              container={document.getElementById(component?.root || '')}
              debug={component?.debug}
              user={data.user}
            />
          </Elements>
        )

      case component.name === 'NewsletterForm':
        return (
          <NewsletterForm
            downloadUrl={data?.download_url}
            downloadName={data?.download_name}
            buttonText={data?.button_text}
            mailingList={data?.mailing_list}
          />
        )

      case component.name === 'CompanyNewsletterForm':
        return (
          <CompanyNewsletterForm
            downloadUrl={data?.download_url}
            downloadName={data?.download_name}
            buttonText={data?.button_text}
            mailingList={data?.mailing_list}
          />
        )

      case component.name === 'PriceBlockWithQuantity':
        return (
          <Portal
            Component={PriceBlockWithQuantity}
            container={document.getElementById(component?.root || '')}
            product={data.product}
            awaitedProductIds={data.awaited_product_ids}
            returnPolicy={data.return_policy}
            shippingPolicy={data.shipping_policy}
            debug={component?.debug}
            customerIsMember={data.customer_is_member}
            accessMembershipPrice={data.access_membership_price}
            annualAccessMembershipPrice={data.annual_access_membership_price}
            showSubscribeButton={data.show_subscribe_button}
            defaultMembershipOption={data.default_membership_option}
            membershipPlans={data.membership_plans}
            membershipPlansEnabled={data.membership_plans_enabled}
            contractPricingData={data.contract_pricing_data}
          />
        )

      case component.name === 'PriceBlockWithoutQuantity':
        return (
          <Portal
            Component={PriceBlockWithoutQuantity}
            container={document.getElementById(component?.root || '')}
            product={data.product}
            returnPolicy={data.return_policy}
            shippingPolicy={data.shipping_policy}
            planTerms={data.plan_terms}
            applicationUrl={data.application_url}
            debug={component?.debug}
            customerIsMember={data.customer_is_member}
            accessMembershipPrice={data.access_membership_price}
            annualAccessMembershipPrice={data.annual_access_membership_price}
            serviceFee={data.service_fee}
            labCompanyName={data.lab_company_name}
            defaultMembershipOption={data.default_membership_option}
            membershipPlans={data.membership_plans}
            membershipPlansEnabled={data.membership_plans_enabled}
            embedded={data.embedded}
          />
        )

      case component.name === 'ProductCardButtons':
        return (
          <Portal
            Component={ProductCardButtons}
            container={document.getElementById(component?.root || '')}
            product={data.product}
            awaitedProductIds={data.awaited_product_ids}
            text={data?.text}
            textAlt={data?.text_alt}
            productUrl={data?.product_url}
            showSubscribeButton={data.show_subscribe_button}
          />
        )

      case component.name === 'AddToCartButton':
        return (
          <Portal
            Component={AddToCartButton}
            container={document.getElementById(component?.root || '')}
            product={data.product}
            awaitedProductIds={data.awaited_product_ids}
            text={data?.text}
            textAlt={data?.text_alt}
            productUrl={data?.product_url}
            showSubscribeButton={data.show_subscribe_button}
          />
        )

      case component.name === 'ProductUtilityActions':
        return (
          <Portal
            Component={ProductUtilityActions}
            container={document.getElementById(component?.root || '')}
            product={data.product}
            customerIsMember={data.customer_is_member}
            showSubscribeButton={data.show_subscribe_button}
            debug={component?.debug}
          />
        )

      case component.name === 'Recommendations':
        return (
          <Portal
            Component={Recommendations}
            container={document.getElementById(component?.root || '')}
            recommendations={data}
            debug={component?.debug}
            title={component?.title}
            showMemberPricing={component?.show_member_pricing}
            supplementInvoicing={component?.supplement_invoicing}
            contractPricingData={component?.contract_pricing_data}
            virtualVisitCoverage={component?.virtual_visit_coverage}
          />
        )

      case component.name === 'AutoAddToCart':
        return (
          <AutoAddToCart
            product={data.product}
            subscription={data.subscription}
          />
        )

      // Rendered in place rather than through Portal: the mount sits inside
      // the profile form, and an input the browser does not find in the form
      // is not submitted.
      case component.name === 'PhoneInput':
        return (
          <PhoneInput
            resource_name={data.resource_name}
            current_phone={data.current_phone}
            invalid_class_name={data.invalid_class_name}
          />
        )

      case component.name === 'StateLicenseMap':
        return (
          <StateLicenseMap licensedStates={data.licensed_states} />
        )

      default:
        return ''
    }
  }

  return (
    process.env.RAILS_ENV === 'test' ? (
      <Provider store={store}>
        <Suspense fallback="">
          {renderComponent()}
        </Suspense>
      </Provider>
    ) : (
      <Provider store={store}>
        <GoogleReCaptchaProvider>
          <GoogleReCaptchaVerifier />
          <Suspense fallback="">
            {renderComponent()}
          </Suspense>
        </GoogleReCaptchaProvider>
      </Provider>
    )
  )
}
